import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider, type PaletteMode } from '@mui/material';
import { createArchiveTheme, themeStorageKey } from './archiveTheme';
import { ThemeModeContext, type ThemeModeValue } from './ThemeModeContext';

function initialMode(): PaletteMode {
  const stored = localStorage.getItem(themeStorageKey);
  return stored === 'blue' || stored === 'dark' ? 'dark' : 'light';
}

export function ArchiveThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<PaletteMode>(initialMode);
  const theme = useMemo(() => createArchiveTheme(mode), [mode]);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', mode === 'dark' ? '#020617' : '#F7F9FF');
  }, [mode]);

  const value = useMemo<ThemeModeValue>(() => ({
    mode,
    toggleMode: () => setMode((current) => {
      const next = current === 'light' ? 'dark' : 'light';
      // Keep the same values as i2x-vf-dashboard, so both products understand
      // an existing company theme preference when they share an origin.
      localStorage.setItem(themeStorageKey, next === 'dark' ? 'blue' : 'light');
      return next;
    })
  }), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
